import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, ArrowRight, Calendar, Clock, Globe, GraduationCap, Send, Loader2, Check, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admissions = () => {
  const [selectedProgram, setSelectedProgram] = useState('masters');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [applicationNumber, setApplicationNumber] = useState('');
  
  const [formData, setFormData] = useState({
    // Personal Information
    full_name: '',
    email: '',
    phone: '',
    nationality: '',
    country_of_residence: '',
    date_of_birth: '',
    gender: '',
    // Program Selection
    program: 'masters',
    scholarship_application: false,
    // Educational Background
    highest_degree: '',
    institution: '',
    field_of_study: '',
    graduation_year: '',
    // Professional Experience
    current_occupation: '',
    organization: '',
    years_of_experience: '',
    // Motivation
    motivation: '',
    career_goals: '',
    // Additional
    how_did_you_hear: '',
    additional_info: ''
  });

  const mastersRequirements = [
    "Bachelor's degree or equivalent qualification from an accredited institution",
    'Proficiency in English (program is delivered entirely in English)',
    'Access to reliable internet connection for online learning',
    'Commitment to completing the 1-year program',
    'Valid identification document (passport or national ID)',
    'For scholarship applicants: Citizen of an African or Asian country'
  ];

  const ppmRequirements = [
    "Bachelor's degree or equivalent qualification from an accredited institution",
    'Proficiency in English (program is delivered entirely in English)',
    'Interest in development project and program management',
    'Access to reliable internet connection for online learning',
    'Commitment to completing the 9-month program',
    'Valid identification document (passport or national ID)',
    'For scholarship applicants: Citizen of an African or Asian country'
  ];

  const doctoralRequirements = [
    "Master's degree in a relevant field from an accredited institution",
    '3-5 years of professional experience in humanitarian or development sector',
    'Proficiency in English (program is delivered entirely in English)',
    'Demonstrated research aptitude and commitment',
    'Access to reliable internet connection for online learning',
    'Valid identification document (passport or national ID)',
    'For scholarship applicants: Citizen of an African or Asian country'
  ];

  const certificateRequirements = [
    'Interest in AI tools for humanitarian and development work',
    'Proficiency in English (course is delivered entirely in English)',
    'Access to reliable internet connection for online learning',
    'Commitment to completing the 4-week course',
    'Valid identification document (passport or national ID)',
    'No prior programming or coding experience required',
    'Note: The 90% Afro-Asia Scholarship does not apply to short courses'
  ];

  const getRequirements = () => {
    switch (selectedProgram) {
      case 'masters':
        return mastersRequirements;
      case 'masters-ppm':
        return ppmRequirements;
      case 'doctoral':
        return doctoralRequirements;
      case 'certificate-ai':
        return certificateRequirements;
      default:
        return mastersRequirements;
    }
  };

  const requirements = getRequirements();

  const applicationDocuments = [
    {
      title: 'Personal Information',
      description: 'Full name, contact details, nationality, and current employment'
    },
    {
      title: 'Educational Background',
      description: 'Degree certificates, transcripts, and academic qualifications'
    },
    {
      title: 'Professional Experience',
      description: 'CV/Resume highlighting relevant humanitarian or development work'
    },
    {
      title: 'Motivation Letter',
      description: 'Brief letter explaining your interest in humanitarian work (optional)'
    },
    {
      title: 'Identification',
      description: 'Passport or national ID for verification purposes'
    }
  ];

  const timeline = [
    { phase: 'Application Submission', description: 'Complete the online application form' },
    { phase: 'Document Review', description: 'Our team reviews your application (1-2 weeks)' },
    { phase: 'Scholarship Decision', description: 'Notification of scholarship status if applicable' },
    { phase: 'Admission Offer', description: 'Receive your official admission letter' },
    { phase: 'Fee Payment', description: 'Pay tuition fee (scholarship or full amount)' },
    { phase: 'Program Start', description: 'Begin your learning journey on the LMS' }
  ];

  const programs = [
    {
      id: 'masters',
      title: 'Master of Humanitarian Action & Development',
      duration: '1 Year',
      fullFee: 'USD 2,000'
    },
    {
      id: 'masters-ppm',
      title: 'Master of Development Project & Program Management',
      duration: '9 Months',
      fullFee: 'USD 3,500'
    },
    {
      id: 'doctoral',
      title: 'Doctor of Humanitarian Action & Development',
      duration: '3 Years',
      fullFee: 'USD 10,800'
    },
    {
      id: 'certificate-ai',
      title: 'AI in Humanitarian Action & Development',
      duration: '4 Weeks',
      fullFee: 'USD 99'
    }
  ];

  const programCopy = {
    'masters': {
      heading: 'Master of Humanitarian Action & Development — Requirements',
      description: 'To ensure all candidates are prepared for the rigours of this one-year Master\u2019s program, please review these requirements carefully before beginning your application.'
    },
    'masters-ppm': {
      heading: 'Master of Development Project & Program Management — Requirements',
      description: 'This nine-month, professionally focused Master\u2019s program is designed for learners advancing in project and program management within development, NGO, public-sector, and social-impact settings.'
    },
    'doctoral': {
      heading: 'Doctor of Humanitarian Action & Development — Requirements',
      description: 'The Doctoral program is designed for experienced professionals seeking to deepen research and leadership expertise. Please ensure you meet these requirements before applying.'
    },
    'certificate-ai': {
      heading: 'AI in Humanitarian Action & Development — Requirements',
      description: 'This four-week short professional course is open to humanitarian, development, NGO, public-sector, and social-impact professionals — no prior technical or AI experience required.'
    }
  };
  const currentCopy = programCopy[selectedProgram] || programCopy['masters'];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      // Scholarship not available for AI short course — reset if applicant switches to it
      if (name === 'program' && value === 'certificate-ai') {
        next.scholarship_application = false;
      }
      return next;
    });
  };

  const handleProgramChange = (programId) => {
    setSelectedProgram(programId);
    setFormData(prev => ({
      ...prev,
      program: programId,
      scholarship_application: programId === 'certificate-ai' ? false : prev.scholarship_application
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit application');
      }

      setApplicationNumber(data.application_number);
      setSubmitSuccess(true);
      // Scroll to the success message
      setTimeout(() => {
        document.getElementById('application-form').scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setSubmitSuccess(false);
    setSubmitError('');
    setApplicationNumber('');
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      nationality: '',
      country_of_residence: '',
      date_of_birth: '',
      gender: '',
      program: 'masters',
      scholarship_application: false,
      highest_degree: '',
      institution: '',
      field_of_study: '',
      graduation_year: '',
      current_occupation: '',
      organization: '',
      years_of_experience: '',
      motivation: '',
      career_goals: '',
      how_did_you_hear: '',
      additional_info: ''
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admissions-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Join Us
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              How to Apply
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
              Ready to take the next step in your humanitarian career? Apply for our 
              Master's or Doctoral program and join a global community of change-makers.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Calendar size={16} className="text-[#163A59]" />
                <span>Rolling Admissions</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock size={16} className="text-[#163A59]" />
                <span>1-2 Weeks Processing</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Globe size={16} className="text-[#163A59]" />
                <span>100% Online Application</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Apply Now Banner */}
      <section className="py-12 bg-[#163A59] border-t-4 border-[#C82032]" data-testid="apply-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white text-center md:text-left">
              <h3 className="font-['Source_Serif_4'] text-2xl font-bold mb-2">
                Ready to Apply?
              </h3>
              <p className="text-white/85">
                Complete our application form below. Eligible Afro-Asia students can apply for 90% scholarship.
              </p>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 rounded-sm font-medium transition-all whitespace-nowrap"
              data-testid="apply-now-main-btn"
            >
              Start Application
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Program Selection */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Choose Your Program
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              Select the Program You're Applying For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
            {programs.map((program) => (
              <button
                key={program.id}
                onClick={() => handleProgramChange(program.id)}
                data-testid={`program-select-${program.id}`}
                className={`p-6 border-2 text-left transition-all ${
                  selectedProgram === program.id
                    ? 'border-[#163A59] bg-[#163A59]/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <GraduationCap className={`w-6 h-6 ${selectedProgram === program.id ? 'text-[#163A59]' : 'text-slate-400'}`} />
                  <h3 className="font-semibold text-slate-900">{program.title}</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Duration</p>
                    <p className="font-medium text-slate-900">{program.duration}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Full Fee</p>
                    <p className="font-medium text-slate-900">{program.fullFee}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Requirements */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Before You Apply
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6" data-testid="program-requirements-heading">
                {currentCopy.heading}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8" data-testid="program-requirements-description">
                {currentCopy.description}
              </p>
              <div className="bg-[#163A59]/5 p-6 border-l-4 border-[#163A59]">
                <p className="font-semibold text-slate-900 mb-2">No Entrance Exam Required</p>
                <p className="text-slate-600 text-sm">
                  We evaluate candidates based on their background, motivation, and 
                  commitment to humanitarian work—not standardized test scores.
                </p>
              </div>
            </div>

            <div className="bg-white">
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <motion.div
                    key={`req-${req.slice(0, 20)}`}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 p-4 bg-[#F1F5F8]"
                  >
                    <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                    <p className="text-slate-600">{req}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Needed */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Prepare Your Documents
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900">
              What You'll Need
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applicationDocuments.map((doc, index) => (
              <motion.div
                key={doc.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 p-8 hover:border-[#163A59]/30 hover:shadow-lg transition-all"
              >
                <div className="w-10 h-10 bg-[#163A59]/10 text-[#163A59] flex items-center justify-center rounded-sm mb-4">
                  <FileText size={20} />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{doc.title}</h3>
                <p className="text-slate-600">{doc.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Timeline */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Application Process
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                From Application to Enrollment
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our streamlined application process is designed to be straightforward and 
                efficient. Here's what to expect from the moment you submit your application 
                to when you begin your studies.
              </p>
            </div>

            <div className="space-y-4">
              {timeline.map((item, index) => (
                <motion.div
                  key={item.phase}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4"
                >
                  <div className="w-8 h-8 bg-[#163A59] text-white flex items-center justify-center rounded-sm font-semibold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="border-b border-slate-200 pb-4 flex-1">
                    <h4 className="font-semibold text-slate-900">{item.phase}</h4>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {!showForm && !submitSuccess ? (
              <div className="text-center">
                <FileText className="w-16 h-16 text-white mx-auto mb-8 opacity-80" />
                <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                  Application Form
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
                  Complete our online application form for both Master's and Doctoral programs. 
                  You will receive a response within 1-2 weeks.
                </p>

                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-10 py-4 rounded-sm font-medium transition-all"
                  data-testid="application-form-btn"
                >
                  Start Application
                  <ArrowRight size={18} />
                </button>
                
                <p className="text-white/60 text-sm mt-6">
                  All fields marked with * are required
                </p>
              </div>
            ) : submitSuccess ? (
              <div className="text-center bg-white p-12 rounded-sm" data-testid="submission-success">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="font-['Source_Serif_4'] text-3xl font-bold text-slate-900 mb-4">
                  Application Submitted!
                </h2>
                <p className="text-slate-600 mb-6">
                  Thank you for your application. Your application number is:
                </p>
                <div className="bg-slate-100 px-6 py-4 inline-block mb-6">
                  <span className="font-mono text-xl font-bold text-[#163A59]" data-testid="application-number">
                    {applicationNumber}
                  </span>
                </div>
                <p className="text-slate-600 mb-8 max-w-md mx-auto">
                  Please save this number for your records. We will review your application and 
                  contact you within 1-2 weeks at your provided email address.
                </p>
                <button
                  onClick={resetForm}
                  className="inline-flex items-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-8 py-3 rounded-sm font-medium transition-all"
                  data-testid="back-to-admissions-btn"
                >
                  Back to Admissions
                </button>
              </div>
            ) : (
              <div className="bg-white p-8 md:p-12 rounded-sm">
                <h2 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-bold text-slate-900 mb-8">
                  Application Form
                </h2>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6 flex items-center gap-2" data-testid="submit-error">
                    <AlertCircle size={18} />
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8" data-testid="application-form-element">
                  {/* Personal Information */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
                      Personal Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="Enter your full name"
                          data-testid="input-full-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="your@email.com"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="+1 234 567 8900"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Date of Birth *
                        </label>
                        <input
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          data-testid="input-dob"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Nationality *
                        </label>
                        <input
                          type="text"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="e.g., Nigerian, Indian, etc."
                          data-testid="input-nationality"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Country of Residence *
                        </label>
                        <input
                          type="text"
                          name="country_of_residence"
                          value={formData.country_of_residence}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="Current country of residence"
                          data-testid="input-country"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Gender *
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          data-testid="input-gender"
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Program Selection */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
                      Program Selection
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Program *
                        </label>
                        <select
                          name="program"
                          value={formData.program}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          data-testid="input-program"
                        >
                          <optgroup label="Degree Programs">
                            <option value="masters">Master of Humanitarian Action & Development (1 Year)</option>
                            <option value="masters-ppm">Master of Development Project & Program Management (9 Months)</option>
                            <option value="doctoral">Doctor of Humanitarian Action & Development (3 Years)</option>
                          </optgroup>
                          <optgroup label="Short Courses">
                            <option value="certificate-ai">AI in Humanitarian Action & Development (4 Weeks - $99)</option>
                          </optgroup>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <label className={`flex items-center gap-3 ${formData.program === 'certificate-ai' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            name="scholarship_application"
                            checked={formData.scholarship_application && formData.program !== 'certificate-ai'}
                            onChange={handleInputChange}
                            disabled={formData.program === 'certificate-ai'}
                            className="w-5 h-5 text-[#163A59] border-slate-300 rounded focus:ring-[#163A59] disabled:cursor-not-allowed"
                            data-testid="input-scholarship"
                          />
                          <span className="text-sm text-slate-700">
                            I am applying for the 90% Afro-Asia Scholarship
                            {formData.program === 'certificate-ai' && (
                              <span className="block text-xs text-slate-500 mt-0.5">Not available for the AI short course</span>
                            )}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Educational Background */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
                      Educational Background
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Highest Degree Obtained *
                        </label>
                        <select
                          name="highest_degree"
                          value={formData.highest_degree}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          data-testid="input-degree"
                        >
                          <option value="">Select degree</option>
                          <option value="bachelors">Bachelor's Degree</option>
                          <option value="masters">Master's Degree</option>
                          <option value="doctorate">Doctorate</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Institution Name *
                        </label>
                        <input
                          type="text"
                          name="institution"
                          value={formData.institution}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="University/College name"
                          data-testid="input-institution"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Field of Study *
                        </label>
                        <input
                          type="text"
                          name="field_of_study"
                          value={formData.field_of_study}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="e.g., International Relations, Public Health"
                          data-testid="input-field-of-study"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Graduation Year *
                        </label>
                        <input
                          type="text"
                          name="graduation_year"
                          value={formData.graduation_year}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="e.g., 2020"
                          data-testid="input-graduation-year"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Professional Experience */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
                      Professional Experience
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Current Occupation *
                        </label>
                        <input
                          type="text"
                          name="current_occupation"
                          value={formData.current_occupation}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="Your current job title"
                          data-testid="input-occupation"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Organization/Company *
                        </label>
                        <input
                          type="text"
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          placeholder="Current employer or organization"
                          data-testid="input-organization"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Years of Experience *
                        </label>
                        <select
                          name="years_of_experience"
                          value={formData.years_of_experience}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          data-testid="input-experience"
                        >
                          <option value="">Select experience</option>
                          <option value="0-2">0-2 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="6-10">6-10 years</option>
                          <option value="10+">10+ years</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Motivation */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
                      Motivation & Goals
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Why are you interested in humanitarian work? *
                        </label>
                        <textarea
                          name="motivation"
                          value={formData.motivation}
                          onChange={handleInputChange}
                          required
                          rows={4}
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59] resize-none"
                          placeholder="Tell us about your motivation for pursuing this program..."
                          data-testid="input-motivation"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          What are your career goals after completing the program? *
                        </label>
                        <textarea
                          name="career_goals"
                          value={formData.career_goals}
                          onChange={handleInputChange}
                          required
                          rows={4}
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59] resize-none"
                          placeholder="Describe your career aspirations..."
                          data-testid="input-career-goals"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
                      Additional Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          How did you hear about us? *
                        </label>
                        <select
                          name="how_did_you_hear"
                          value={formData.how_did_you_hear}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59]"
                          data-testid="input-how-heard"
                        >
                          <option value="">Select option</option>
                          <option value="search_engine">Search Engine (Google, Bing)</option>
                          <option value="social_media">Social Media</option>
                          <option value="friend_colleague">Friend or Colleague</option>
                          <option value="news_article">News Article</option>
                          <option value="advertisement">Advertisement</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Additional Information (Optional)
                        </label>
                        <textarea
                          name="additional_info"
                          value={formData.additional_info}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#163A59] resize-none"
                          placeholder="Any additional information you'd like to share..."
                          data-testid="input-additional-info"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] disabled:bg-slate-400 text-white px-8 py-4 rounded-sm font-medium transition-all"
                      data-testid="submit-application-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting Application...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Submit Application
                        </>
                      )}
                    </button>
                    <p className="text-xs text-slate-500 text-center mt-4">
                      By submitting this application, you agree to our{' '}
                      <Link to="/privacy-policy" className="text-[#163A59] hover:underline">Privacy Policy</Link>{' '}
                      and{' '}
                      <Link to="/terms-of-service" className="text-[#163A59] hover:underline">Terms of Service</Link>.
                    </p>
                  </div>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Contact for Questions */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            Have Questions About Admissions?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10">
            Our admissions team is here to help. Reach out with any questions about 
            the application process, requirements, or scholarship eligibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-8 py-4 rounded-sm font-medium transition-all"
              data-testid="admissions-contact-btn"
            >
              Contact Admissions
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-900 hover:bg-[#F1F5F8] px-8 py-4 rounded-sm font-medium transition-all"
              data-testid="admissions-faq-btn"
            >
              View FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
